import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getAcarsStats, getAirbandStats,
  getReceiverDailyBreakdown, getReceiverHourlyBreakdown,
} from '../utils/httpApi';
import { getAccessToken } from '../utils/auth';
import { isApiSuccess } from '../utils/api';
import { useSettings } from '../contexts/SettingsContext';
import { fmtInt, fmtPercent } from '../utils/statsFormat';
import { useReceiverStats } from '../hooks/useReceiverStats';
import SiteSection from './statistics/SiteSection';
import LiveHealthSection from './statistics/LiveHealthSection';
import AdsbSection from './statistics/AdsbSection';
import RangeControl from './statistics/RangeControl';
import { DEFAULT_RANGE, dailyRows, hourlyRows, resolveRange, singleDayNote } from './statistics/range';
import BarChart from './charts/BarChart';
import ChartCard from './charts/ChartCard';
import './statistics/statistics.css';

/**
 * Per-receiver Statistics page.
 *
 * Sections, not tabs. Tabs hid "does this receiver do both?" behind a click and
 * made an ACARS-only site look like an ADS-B site with no data. One filter row
 * scopes everything below it, and a section renders only when the receiver
 * actually has that kind of data.
 *
 * Migration state: Site, Live health and ADS-B run on the chart kit in
 * components/charts. ACARS/VDL2 and Airband use the interim SourceSection at
 * the bottom of this file — same endpoints and the same kit, but not yet split
 * into their own files with the full tile sets.
 */
function Statistics({
  receivers = [],
  viewerReceivers = [],
  selectedReceiverUuid,
  onReceiverChange,
  receiverStatistics = null,
}) {
  const allReceivers = useMemo(
    () => [...receivers, ...viewerReceivers], [receivers, viewerReceivers]);
  const { unitsSettings } = useSettings();
  const unitSystem = unitsSettings.units;

  const [localReceiverUuid, setLocalReceiverUuid] = useState(
    selectedReceiverUuid && selectedReceiverUuid !== 'ALL_RECEIVERS' ? selectedReceiverUuid : '');
  const [rangeSel, setRangeSel] = useState(DEFAULT_RANGE);
  const [refreshSeconds, setRefreshSeconds] = useState(0);

  // Re-resolved when the selection changes: "today" moves with the clock, so
  // this must not be frozen into state.
  const range = useMemo(() => resolveRange(rangeSel), [rangeSel]);

  const stats = useReceiverStats(localReceiverUuid, range, refreshSeconds);
  const { caps, overview } = stats;

  // Follow the app-wide receiver, or default to the first available.
  useEffect(() => {
    if (selectedReceiverUuid && selectedReceiverUuid !== 'ALL_RECEIVERS') {
      setLocalReceiverUuid(selectedReceiverUuid);
    } else if (!localReceiverUuid && allReceivers.length > 0) {
      setLocalReceiverUuid(allReceivers[0].receiverUuid);
    }
  }, [selectedReceiverUuid, allReceivers, localReceiverUuid]);

  const handleReceiverChange = useCallback((e) => {
    const uuid = e.target.value;
    setLocalReceiverUuid(uuid);
    const r = allReceivers.find(x => x.receiverUuid === uuid);
    if (r && onReceiverChange) onReceiverChange(r.receiverName);
  }, [allReceivers, onReceiverChange]);

  // Today's headline numbers ride the periodic `receiverStatistics` broadcast —
  // the same payload the map HUDs read, so the figures always agree and this
  // page costs no extra request for them.
  const todayStats = useMemo(() => {
    if (!receiverStatistics?.receivers || !localReceiverUuid) return null;
    return receiverStatistics.receivers.find(r => r.receiverUuid === localReceiverUuid) || null;
  }, [receiverStatistics, localReceiverUuid]);

  // Gate ADS-B statistics on adsbData (tracks exist), NOT the combined adsb
  // flag: a VDL2-only receiver sends STATS heartbeats, so the old flag was true
  // for it and it was shown a screen of permanently-empty ADS-B charts.
  // Fall back to `adsb` so this still works against an older backend.
  const showAdsb = caps ? (caps.adsbData != null ? caps.adsbData : caps.adsb) : false;
  // `stats.latest` is now served from stored history when the receiver is not reporting
  // (V085), so OR it in rather than letting a false `adsbLive` delete the whole section.
  // Previously an offline receiver lost Live health silently — no heading, no empty state,
  // just a section that was there yesterday and gone today. The section stamps its own
  // staleness, so showing it with historical values is strictly better than hiding it.
  const showLive = caps
    ? ((caps.adsbLive != null ? !!caps.adsbLive : false) || !!stats.latest)
    : !!stats.latest;
  const showAcars = !!caps?.acars;
  const showAirband = !!caps?.airband;

  const sections = [
    ['site', 'Site', true],
    ['live', 'Live health', showLive],
    ['adsb', 'ADS-B', showAdsb],
    ['acars', 'ACARS / VDL2', showAcars],
    ['airband', 'Airband', showAirband],
  ].filter(([, , on]) => on);

  return (
    <div className="stats-page">
      <RangeControl
        receivers={allReceivers}
        receiverUuid={localReceiverUuid}
        onReceiverChange={handleReceiverChange}
        range={rangeSel}
        onRangeChange={setRangeSel}
        refreshSeconds={refreshSeconds}
        onRefreshChange={setRefreshSeconds}
        onRefreshNow={stats.reload}
        loading={stats.loading}
        updatedAt={stats.updatedAt}
      />

      {!localReceiverUuid && (
        <div className="stats-empty">Select a receiver to view its statistics.</div>
      )}

      {stats.error && <div className="stats-error">{stats.error}</div>}

      {localReceiverUuid && sections.length > 1 && (
        <nav className="stats-rail" aria-label="Sections">
          {sections.map(([id, label]) => (
            <a className="stats-rail-link" href={`#${id}`} key={id}>{label}</a>
          ))}
        </nav>
      )}

      {localReceiverUuid && (
        <SiteSection overview={overview} todayStats={todayStats} unitSystem={unitSystem} />
      )}

      {localReceiverUuid && showLive && (
        <LiveHealthSection
          series={stats.series}
          latest={stats.latest}
          events={stats.events}
          range={range}
          loading={stats.loading}
        />
      )}

      {localReceiverUuid && showAdsb && (
        <AdsbSection
          daily={stats.daily}
          hourly={stats.hourly}
          coverage={stats.coverage}
          reportHeatmap={stats.reportHeatmap}
          reportLoading={stats.reportLoading}
          dayAircraft={stats.dayAircraft}
          dayRangeSamples={stats.dayRangeSamples}
          range={range}
          loading={stats.loading}
        />
      )}

      {localReceiverUuid && showAcars && (
        <SourceSection id="acars" heading="ACARS / VDL2" source="acars"
                       receiverUuid={localReceiverUuid} range={range} />
      )}

      {localReceiverUuid && showAirband && (
        <SourceSection id="airband" heading="Airband" source="airband"
                       receiverUuid={localReceiverUuid} range={range} />
      )}
    </div>
  );
}

/**
 * Interim section for ACARS and Airband: they share the daily/hourly endpoints
 * with ADS-B via a different `source`, so one component covers both until they
 * get their own files.
 */
function SourceSection({ id, heading, source, receiverUuid, range }) {
  const [daily, setDaily] = useState([]);
  const [hourly, setHourly] = useState([]);
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!receiverUuid) return undefined;
    let cancelled = false;
    setLoading(true);
    const token = getAccessToken();
    const todayCall = source === 'acars'
      ? getAcarsStats(receiverUuid, null, token)
      : getAirbandStats(receiverUuid, null, token);

    Promise.allSettled([
      getReceiverDailyBreakdown(receiverUuid, source, range.days, token),
      getReceiverHourlyBreakdown(receiverUuid, source, range.day, token),
      todayCall,
    ]).then(res => {
      if (cancelled) return;
      const ok = (i) => (res[i].status === 'fulfilled' && isApiSuccess(res[i].value))
        ? res[i].value : null;
      setDaily(ok(0)?.buckets || []);
      setHourly(ok(1)?.buckets || []);
      setToday(ok(2));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [receiverUuid, source, range.days, range.day]);

  const isAcars = source === 'acars';
  const dayNote = singleDayNote(range);

  const perDay = useMemo(() => dailyRows(
    daily,
    isAcars ? ['messages', 'aircraft', 'positions', 'textMessages'] : ['messages', 'matched'],
    range.days), [daily, isAcars, range.days]);

  const perHour = useMemo(() => hourlyRows(
    hourly,
    isAcars ? ['messages', 'aircraft'] : ['messages', 'matched']), [hourly, isAcars]);

  const empty = perDay.every(r => r.values.every(v => !v));
  const channels = (!isAcars && today?.channels) || [];

  return (
    <section className="stats-section" id={id}>
      <header className="stats-section-header">
        <h2 className="stats-section-heading">{heading}</h2>
        <span className="stats-section-sub">{range.label}</span>
      </header>

      {today && (
        <div className="stats-live-strip">
          {isAcars ? (
            <>
              <Tile label="messages today" value={fmtInt(today.totalMessages)} />
              <Tile label="aircraft today" value={fmtInt(today.uniqueAircraft)} />
              <Tile label="positions today" value={fmtInt(today.positions)} />
              <Tile label="text msgs today" value={fmtInt(today.textMessages)} />
              <Tile label="CRC valid" value={fmtPercent(today.crcRate)} />
            </>
          ) : (
            <>
              <Tile label="transmissions today" value={fmtInt(today.totalMessages)} />
              <Tile label="ADS-B matched" value={fmtInt(today.callsignMatched)} />
              <Tile label="channels" value={fmtInt(channels.length)} />
            </>
          )}
        </div>
      )}

      <div className="stats-group-title">Per day</div>
      <div className="chart-grid">
        {isAcars ? (
          <>
            <ChartCard title="Messages per day" loading={loading} empty={empty}>
              <BarChart format={fmtInt} data={pick(perDay, 0)}
                        series={[{ name: 'messages', slot: 1 }]} />
            </ChartCard>
            <ChartCard title="Aircraft per day" loading={loading} empty={empty}>
              <BarChart format={fmtInt} data={pick(perDay, 1)}
                        series={[{ name: 'aircraft', slot: 2 }]} />
            </ChartCard>
            {/* Merged: both are subsets of the same message count, same unit. */}
            <ChartCard title="Message types per day" subtitle="positions vs text"
                       loading={loading} empty={empty}
                       legend={[{ name: 'positions', slot: 3, mark: 'bar' },
                                { name: 'text', slot: 4, mark: 'bar' }]}>
              <BarChart mode="grouped" format={fmtInt} data={pick(perDay, 2, 3)}
                        series={[{ name: 'positions', slot: 3 }, { name: 'text', slot: 4 }]} />
            </ChartCard>
          </>
        ) : (
          /* Merged: matched is a subset of transmissions, so same unit. */
          <ChartCard title="Transmissions per day" subtitle="total vs ADS-B matched"
                     loading={loading} empty={empty} span="2"
                     legend={[{ name: 'transmissions', slot: 1, mark: 'bar' },
                              { name: 'matched', slot: 3, mark: 'bar' }]}>
            <BarChart mode="grouped" format={fmtInt} data={pick(perDay, 0, 1)}
                      series={[{ name: 'transmissions', slot: 1 },
                               { name: 'matched', slot: 3 }]} />
          </ChartCard>
        )}
      </div>

      <div className="stats-group-title">Per hour · UTC</div>
      <div className="chart-grid">
        {isAcars ? (
          <>
            <ChartCard title="Messages per hour" note={dayNote} loading={loading}>
              <BarChart format={fmtInt} maxLabels={12} data={pick(perHour, 0)}
                        series={[{ name: 'messages', slot: 2 }]} />
            </ChartCard>
            <ChartCard title="Aircraft per hour" note={dayNote} loading={loading}>
              <BarChart format={fmtInt} maxLabels={12} data={pick(perHour, 1)}
                        series={[{ name: 'aircraft', slot: 2 }]} />
            </ChartCard>
          </>
        ) : (
          <ChartCard title="Transmissions per hour" subtitle="total vs matched" note={dayNote}
                     loading={loading} span="2"
                     legend={[{ name: 'transmissions', slot: 1, mark: 'bar' },
                              { name: 'matched', slot: 3, mark: 'bar' }]}>
            <BarChart mode="grouped" format={fmtInt} maxLabels={12} data={pick(perHour, 0, 1)}
                      series={[{ name: 'transmissions', slot: 1 },
                               { name: 'matched', slot: 3 }]} />
          </ChartCard>
        )}
      </div>

      {channels.length > 0 && (
        <>
          <div className="stats-group-title">Per channel · today</div>
          <div className="chart-grid">
            <ChartCard title="Transmissions by channel (MHz)" span="full" loading={loading}>
              <BarChart format={fmtInt} maxLabels={16}
                        data={channels.map(c => ({
                          label: (c.channelMhz || 0).toFixed(3),
                          values: [c.messageCount || 0],
                        }))}
                        series={[{ name: 'transmissions', slot: 1 }]} />
            </ChartCard>
          </div>
        </>
      )}
    </section>
  );
}

/** Narrow a multi-value row set down to the given value indices. */
function pick(rows, ...indices) {
  return rows.map(r => ({ label: r.label, values: indices.map(i => r.values[i]) }));
}

function Tile({ label, value }) {
  return (
    <div className="stats-live-stat">
      <div className="stats-live-value">{value}</div>
      <div className="stats-live-label">{label}</div>
    </div>
  );
}

export default Statistics;
